/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Image, Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

interface PropertyGalleryProps {
  images: string[];
  title: string;
  onOpenLightbox: (index: number) => void;
}

/**
 * Sticky photo mosaic for the public property listing.
 */
export function PropertyGallery({ images, title, onOpenLightbox }: PropertyGalleryProps) {
  const { t } = useTranslation('@pages/public-property');
  const thumbCount = Math.min(Math.max(images.length - 1, 0), 4);

  return (
    <div
      className={cn(
        'flex max-h-[calc(100vh-113px)] flex-col gap-(--space-1) overflow-hidden',
        'rounded-(--radius-lg) bg-(--surface-secondary) p-(--space-1)',
        'shadow-[var(--shadow-sm)] lg:sticky lg:top-(--space-5)',
      )}
      data-testid="public-property-gallery"
    >
      {images.length > 0 ? (
        <>
          <UnstyledButton
            type="button"
            className="min-h-[200px] flex-[2] cursor-pointer overflow-hidden rounded-(--radius-md) border-0 bg-transparent p-0"
            onClick={() => onOpenLightbox(0)}
            aria-label={title}
          >
            <Image src={images[0]} alt={title} className="h-full w-full object-cover" />
          </UnstyledButton>
          {images.length > 1 && (
            <div
              className="grid flex-1 gap-(--space-1)"
              style={{ gridTemplateColumns: `repeat(${thumbCount}, 1fr)` }}
            >
              {images.slice(1, 5).map((img, i) => {
                const isLast = i === 3 && images.length > 5;
                return (
                  <UnstyledButton
                    type="button"
                    key={img}
                    className="relative cursor-pointer overflow-hidden rounded-(--radius-md) border-0 bg-transparent p-0"
                    onClick={() => onOpenLightbox(i + 1)}
                  >
                    <Image src={img} alt="" className="h-full w-full object-cover" />
                    {isLast && (
                      <Text component="span" className="absolute inset-0 flex items-center justify-center bg-(--overlay-scrim) text-[length:var(--text-body-sm)] font-semibold text-(--text-inverse)">
                        {t('gallery.morePhotos', { count: images.length - 5 })}
                      </Text>
                    )}
                  </UnstyledButton>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center py-(--space-8)">
          <Text as="span" className="text-[length:var(--text-body-sm)] text-(--text-tertiary)">
            {t('gallery.noPhotos')}
          </Text>
        </div>
      )}
    </div>
  );
}
